if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }
    }
  );
}

if (!customElements.get('quick-add-card')) {
  customElements.define(
    'quick-add-card',
    class QuickAddCard extends HTMLElement {
      static openCard = null;

      constructor() {
        super();
        this.hasMultipleVariants = this.dataset.hasMultipleVariants === 'true';
        this.isPackMode = this.dataset.isPackMode === 'true';
        this.hasOptionSelector = this.hasMultipleVariants || this.isPackMode;
        this.trigger = this.querySelector('.quick-add-card__trigger');
        this.panel = this.querySelector('.quick-add-card__panel');
        this.closeButton = this.querySelector('.quick-add-card__close');
        this.optionButtons = Array.from(this.querySelectorAll('.quick-add-card__option'));
        this.variantInput = this.querySelector('.product-variant-id');
        this.quantityInput = this.querySelector('.quick-add-card__quantity-input');
        this.quantitySelector = this.querySelector('.quick-add-card__qty');
        this.userQuantityInput = this.quantitySelector?.querySelector('.quick-add-card__qty-input');
        this.form = this.querySelector('form');
        this.addButton = this.querySelector('.quick-add-card__add');
        this.triggerSpinner = this.trigger?.querySelector('.loading__spinner');
        this.selectedOptionQuantity = 1;
        this.userQuantity = this.getSanitizedQuantity(this.userQuantityInput?.value);

        this.onDocumentClick = this.handleDocumentClick.bind(this);
        this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);
        this.onTriggerClick = this.handleTriggerClick.bind(this);
        this.onCloseClick = this.handleCloseClick.bind(this);
        this.onFormSubmit = this.handleFormSubmit.bind(this);
        this.onUserQuantityChange = this.handleUserQuantityChange.bind(this);
        this.optionClickHandlers = new Map();
        this.panelMountParent = this.panel?.parentNode || null;
        this.panelMountNextSibling = this.panel?.nextSibling || null;
        this.mobileDrawerCloseTimeout = null;
        this.mobileDrawerLayer = null;
      }

      connectedCallback() {
        if (!this.trigger || !this.form || !this.variantInput) return;
        this.closest('.card-wrapper')?.classList.add('quick-add-card-enabled');

        if (this.hasOptionSelector) {
          this.trigger.addEventListener('click', this.onTriggerClick);
          if (this.closeButton) this.closeButton.addEventListener('click', this.onCloseClick);
          if (this.userQuantityInput) {
            this.userQuantityInput.addEventListener('change', this.onUserQuantityChange);
            this.userQuantityInput.addEventListener('input', this.onUserQuantityChange);
          }
          this.optionButtons.forEach((button) => {
            const onOptionClick = () => this.selectOption(button);
            this.optionClickHandlers.set(button, onOptionClick);
            button.addEventListener('click', onOptionClick);
          });
          this.form.addEventListener('submit', this.onFormSubmit);
          document.addEventListener('click', this.onDocumentClick);
          document.addEventListener('keydown', this.onDocumentKeydown);
          this.initializeSelection();
        } else {
          this.form.addEventListener('submit', this.onFormSubmit);
        }
      }

      disconnectedCallback() {
        this.closest('.card-wrapper')?.classList.remove('quick-add-card-enabled');
        this.form?.removeEventListener('submit', this.onFormSubmit);
        this.stopLoadingSync();
        this.setTriggerLoading(false);
        this.clearMobileDrawerCloseTimeout();
        this.unmountPanelFromMobileDrawer(true);
        if (!this.hasOptionSelector) return;

        this.trigger?.removeEventListener('click', this.onTriggerClick);
        this.closeButton?.removeEventListener('click', this.onCloseClick);
        if (this.userQuantityInput) {
          this.userQuantityInput.removeEventListener('change', this.onUserQuantityChange);
          this.userQuantityInput.removeEventListener('input', this.onUserQuantityChange);
        }
        this.optionClickHandlers.forEach((onOptionClick, button) => {
          button.removeEventListener('click', onOptionClick);
        });
        this.optionClickHandlers.clear();
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onDocumentKeydown);
      }

      handleTriggerClick(event) {
        event.preventDefault();
        this.setOpenState(!this.classList.contains('is-open'));
      }

      handleCloseClick(event) {
        event.preventDefault();
        this.setOpenState(false);
      }

      handleDocumentClick(event) {
        if (!this.classList.contains('is-open')) return;
        const clickedInsidePanel = this.panel?.contains(event.target);
        const clickedTrigger = this.trigger?.contains(event.target);
        if (clickedInsidePanel || clickedTrigger) return;

        // On mobile drawer, taps on the backdrop target this element.
        if (event.target === this) {
          this.setOpenState(false);
          return;
        }

        if (this.contains(event.target)) {
          this.setOpenState(false);
          return;
        }
        this.setOpenState(false);
      }

      handleDocumentKeydown(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;
        if (!this.classList.contains('is-open')) return;
        this.setOpenState(false);
      }

      handleFormSubmit(event) {
        if (this.hasOptionSelector && !this.selectedOptionButton) {
          event.preventDefault();
          return;
        }

        if (!this.hasOptionSelector) return;

        this.setOpenState(false);
        this.setTriggerLoading(true);

        // Wait for ProductForm's submit handler to set loading state.
        window.setTimeout(() => this.startLoadingSync(), 0);
      }

      initializeSelection() {
        this.updateUserQuantity(this.userQuantityInput?.value);
        const firstAvailable = this.optionButtons.find((button) => !button.disabled);
        if (!firstAvailable) {
          this.variantInput.disabled = true;
          if (this.addButton) this.addButton.setAttribute('disabled', 'disabled');
          this.updateHiddenQuantity();
          return;
        }

        this.selectOption(firstAvailable);
      }

      selectOption(button) {
        if (!button || button.disabled) return;

        this.optionButtons.forEach((optionButton) => optionButton.classList.remove('is-selected'));
        button.classList.add('is-selected');
        this.selectedOptionButton = button;
        if (button.dataset.variantId) {
          this.variantInput.value = button.dataset.variantId;
        }
        this.variantInput.disabled = false;
        this.selectedOptionQuantity = this.getSanitizedQuantity(button.dataset.quantity);
        this.updateHiddenQuantity();
        this.addButton?.removeAttribute('disabled');
      }

      handleUserQuantityChange() {
        this.updateUserQuantity(this.userQuantityInput?.value);
      }

      updateUserQuantity(rawQuantity) {
        this.userQuantity = this.getSanitizedQuantity(rawQuantity);
        if (this.userQuantityInput) this.userQuantityInput.value = String(this.userQuantity);
        this.updateHiddenQuantity();
      }

      updateHiddenQuantity() {
        if (!this.quantityInput) return;
        this.quantityInput.value = String(this.selectedOptionQuantity * this.userQuantity);
      }

      getSanitizedQuantity(rawQuantity) {
        const parsedQuantity = Number.parseInt(rawQuantity, 10);
        return Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
      }

      setOpenState(isOpen) {
        if (!this.hasOptionSelector || !this.panel) return;

        if (isOpen && QuickAddCard.openCard && QuickAddCard.openCard !== this) {
          QuickAddCard.openCard.setOpenState(false);
        }

        if (this.isMobileDrawerViewport()) {
          if (isOpen) {
            this.clearMobileDrawerCloseTimeout();
            this.mountPanelToMobileDrawer();
            this.setBaseOpenState(true);
            this.mobileDrawerLayer?.classList.add('is-open');
            return;
          }

          this.mobileDrawerLayer?.classList.remove('is-open');
          this.clearMobileDrawerCloseTimeout();
          this.mobileDrawerCloseTimeout = window.setTimeout(() => {
            this.unmountPanelFromMobileDrawer(true);
            this.setBaseOpenState(false);
          }, 280);
          return;
        }

        this.unmountPanelFromMobileDrawer(true);
        this.setBaseOpenState(isOpen);
      }

      setBaseOpenState(isOpen) {
        this.classList.toggle('is-open', isOpen);
        this.panel.setAttribute('aria-hidden', `${!isOpen}`);
        this.trigger.setAttribute('aria-expanded', `${isOpen}`);
        QuickAddCard.openCard = isOpen ? this : QuickAddCard.openCard === this ? null : QuickAddCard.openCard;
      }

      isMobileDrawerViewport() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      clearMobileDrawerCloseTimeout() {
        if (!this.mobileDrawerCloseTimeout) return;
        clearTimeout(this.mobileDrawerCloseTimeout);
        this.mobileDrawerCloseTimeout = null;
      }

      ensureMobileDrawerLayer() {
        if (this.mobileDrawerLayer) return this.mobileDrawerLayer;
        this.mobileDrawerLayer = document.createElement('div');
        this.mobileDrawerLayer.className = 'quick-add-card__drawer-layer';
        return this.mobileDrawerLayer;
      }

      mountPanelToMobileDrawer() {
        if (!this.panel) return;
        const layer = this.ensureMobileDrawerLayer();
        if (!document.body.contains(layer)) document.body.appendChild(layer);
        if (!layer.contains(this.panel)) layer.appendChild(this.panel);
      }

      unmountPanelFromMobileDrawer(removeLayer) {
        if (!this.panelMountParent || !this.panel) return;
        if (this.panelMountNextSibling) {
          this.panelMountParent.insertBefore(this.panel, this.panelMountNextSibling);
        } else {
          this.panelMountParent.appendChild(this.panel);
        }
        if (removeLayer && this.mobileDrawerLayer?.parentNode) {
          this.mobileDrawerLayer.parentNode.removeChild(this.mobileDrawerLayer);
        }
      }

      setTriggerLoading(isLoading) {
        if (!this.trigger) return;
        this.trigger.classList.toggle('loading', isLoading);
        if (this.triggerSpinner) this.triggerSpinner.classList.toggle('hidden', !isLoading);
      }

      startLoadingSync() {
        if (!this.addButton) {
          this.setTriggerLoading(false);
          return;
        }

        this.stopLoadingSync();
        this.loadingObserver = new MutationObserver(() => this.syncLoadingState());
        this.loadingObserver.observe(this.addButton, {
          attributes: true,
          attributeFilter: ['class'],
        });
        this.syncLoadingState();
      }

      stopLoadingSync() {
        if (!this.loadingObserver) return;
        this.loadingObserver.disconnect();
        this.loadingObserver = null;
      }

      syncLoadingState() {
        if (!this.addButton || !this.addButton.classList.contains('loading')) {
          this.setTriggerLoading(false);
          this.stopLoadingSync();
        }
      }
    }
  );
}
